'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DockPosition, readDockIconPanels, saveDockIconPanels } from '@/utils/dockPrefs';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import useMainMenuPermissions from '../permissions/useMainMenuFormPermissions';
const PANELS: {
  pos: DockPosition;
  label: string;
  color: string;
  border: string;
  bg: string;
  badge: string;
}[] = [{
  pos: 'left',
  label: 'Left Panel',
  color: 'text-blue-700',
  border: 'border-blue-200',
  bg: 'bg-blue-50',
  badge: 'bg-blue-100 text-blue-700 hover:bg-blue-200'
}, {
  pos: 'bottom',
  label: 'Bottom Panel',
  color: 'text-emerald-700',
  border: 'border-emerald-200',
  bg: 'bg-emerald-50',
  badge: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
}, {
  pos: 'right',
  label: 'Right Panel',
  color: 'text-violet-700',
  border: 'border-violet-200',
  bg: 'bg-violet-50',
  badge: 'bg-violet-100 text-violet-700 hover:bg-violet-200'
}];
interface QuickMenuSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}
const QuickMenuSettings = ({
  isOpen,
  onClose
}: QuickMenuSettingsProps) => {
  const {
    menuItems,
    isLoading
  } = useMainMenuPermissions();
  const user = useCurrentUser();
  const [iconPanels, setIconPanels] = useState<Record<string, DockPosition>>({});
  useEffect(() => {
    if (isOpen && user?.id) setIconPanels(readDockIconPanels(user.id));
  }, [isOpen, user?.id]);
  const assign = (componentKey: string, panel: DockPosition) => {
    if (!user?.id) return;
    const next = {
      ...iconPanels,
      [componentKey]: panel
    };
    setIconPanels(next);
    saveDockIconPanels(user.id, next);
    window.dispatchEvent(new CustomEvent('dockIconPanelsChanged'));
  };
  const filteredItems = Object.entries(menuItems);

  // 80px keeps the dialog clear of the fixed header — same clearance used by the quick-menu docks
  const dialogPosition = typeof window !== 'undefined' ? {
    x: Math.max(16, window.innerWidth / 2 - 480),
    y: Math.max(80, window.innerHeight / 2 - 295)
  } : undefined;
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open) onClose();
  }} data-sentry-element="Dialog" data-sentry-component="QuickMenuSettings" data-sentry-source-file="QuickMenuSettings.tsx">
      <DialogContent className="max-h-[calc(100vh-96px)] w-[960px] max-w-[calc(100vw-32px)] overflow-y-auto" defaultPosition={dialogPosition} data-sentry-element="DialogContent" data-sentry-source-file="QuickMenuSettings.tsx">
        <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="QuickMenuSettings.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="QuickMenuSettings.tsx">Quick Menu Layout</DialogTitle>
          <p className="mt-0.5 text-xs text-gray-400">
            Move items between panels — changes apply to the dock immediately.
          </p>
        </DialogHeader>

        {isLoading ? <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading...</div> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {PANELS.map(panel => {
          const panelItems = filteredItems.filter(([, m]) => (iconPanels[m.module] ?? 'bottom') === panel.pos);
          const otherPanels = PANELS.filter(p => p.pos !== panel.pos);
          return <div key={panel.pos} className="flex min-w-0 flex-col gap-2">
                  {/* Column header */}
                  <div className={cn('rounded-lg px-3 py-2.5 text-center', panel.bg)}>
                    <span className={cn('text-sm font-semibold', panel.color)}>{panel.label}</span>
                    <span className="ml-2 rounded-full bg-white/60 px-1.5 py-0.5 text-xs font-medium text-gray-500">
                      {panelItems.length}
                    </span>
                  </div>

                  {/* Drop zone — outer holds border, inner scrolls (avoids scrollbar/border overlap) */}
                  <div className={cn('h-[260px] rounded-xl border-2 border-dashed p-1 sm:h-[420px]', panel.border)}>
                    <div className="flex h-full flex-col gap-1.5 overflow-y-auto rounded-lg p-2">
                      {panelItems.length === 0 ? <div className="flex h-full items-center justify-center text-xs text-gray-400">
                          No items assigned
                        </div> : panelItems.map(([name, item]) => {
                  const IconComponent = item.icon;
                  return <div key={name} className="flex items-center gap-2.5 rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-gray-100">
                              {IconComponent && <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-600">
                                  <IconComponent />
                                </span>}
                              <span className="flex-1 truncate text-sm font-medium text-[#212121]">{item.name}</span>
                              {/* Move-to buttons for the other two panels */}
                              <div className="flex flex-shrink-0 gap-1">
                                {otherPanels.map(target => <button key={target.pos} title={`Move to ${target.label}`} onClick={() => assign(item.module, target.pos)} className={cn('rounded px-3 py-1 text-xs font-bold uppercase transition-colors', target.badge)}>
                                    {target.pos[0].toUpperCase()}
                                  </button>)}
                              </div>
                            </div>;
                })}
                    </div>
                  </div>
                </div>;
        })}
          </div>}

        <div className="flex items-center justify-between pt-1">
          <button onClick={() => {
          if (!user?.id) return;
          localStorage.removeItem(`dockIconPanels_${user.id}`);
          setIconPanels({});
          window.dispatchEvent(new CustomEvent('dockIconPanelsChanged'));
        }} className="text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline">
            Reset to defaults
          </button>
          <button onClick={onClose} className="rounded-md bg-[#212121] px-6 py-1.5 text-sm font-medium text-white hover:bg-[#333]">
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>;
};
export default QuickMenuSettings;